import { fetchWithAuth } from "../auth/auth";
import type { AnalysisResponse, HearingTestResultPayload } from "../type/result";
import type { VerbalTestResultPayload } from "../type/verbalTest";
import type { AgeCategory, HeadphoneType } from "../type/demographics";
import { AGE_CATEGORY_OPTIONS, HEADPHONE_TYPE_OPTIONS } from "../type/demographics";
import { getApiUrl } from "./getApiUrl";

const ANALYSIS_THRESHOLD_DB = 5;

// ── Demographics passed alongside a result, used to enrich the AI prompt ─────
export type ResultAnalysisDemographics = {
  ageCategory?: AgeCategory;
  headphoneType?: HeadphoneType;
};

const buildDemographicsLines = (demographics?: ResultAnalysisDemographics): string[] => {
  const lines: string[] = [];

  const ageLabel = AGE_CATEGORY_OPTIONS.find((option) => option.value === demographics?.ageCategory)?.label;
  if (demographics?.ageCategory && demographics.ageCategory !== "unspecified" && ageLabel) {
    lines.push(`Wiek badanego: ${ageLabel} lat.`);
  }

  const headphoneLabel = HEADPHONE_TYPE_OPTIONS.find((option) => option.value === demographics?.headphoneType)?.label;
  if (demographics?.headphoneType && demographics.headphoneType !== "unspecified" && headphoneLabel) {
    lines.push(`Rodzaj słuchawek użytych podczas badania: ${headphoneLabel}.`);
  }

  return lines;
};

export const canAnalyzeHearingResult = (result: Pick<HearingTestResultPayload, "left" | "right">) => (
  [...result.left, ...result.right].some((value) => value > ANALYSIS_THRESHOLD_DB)
);

export const buildHearingResultAnalysisPrompt = (result: HearingTestResultPayload, demographics?: ResultAnalysisDemographics) => {
  const frequencyLine = result.frequencies.join(", ");
  const leftLine = result.left.join(", ");
  const rightLine = result.right.join(", ");
  const demographicsLines = buildDemographicsLines(demographics);

  return [
    "Przeanalizuj wynik badania słuchu na podstawie audiogramu.",
    "Odpowiedz po polsku, używając prostego, zrozumiałego języka dla pacjenta (bez żargonu medycznego).",
    
    "Założenia:",
    "Wyższa wartość dB HL oznacza gorszy słuch. Dla potrzeb tego testu orientacyjna norma wynosi 0–20 dB HL.",
    "Interpretuj wynik przede wszystkim na podstawie wartości progów słyszenia dla poszczególnych częstotliwości oraz różnic pomiędzy uszami.",
    "Nie traktuj wartości 0–20 dB HL jako absolutnej granicy prawidłowego słuchu – wynik należy interpretować w kontekście całego audiogramu, wieku badanego oraz jakości wykonanego pomiaru.",

    "Ważne:",
    "Nie sugeruj jednoznacznie, że wynik jest prawidłowy – uwzględnij, że na wynik mogły wpłynąć czynniki takie jak słabej jakości słuchawki, hałas w otoczeniu, niewłaściwe dopasowanie słuchawek, nieprawidłowe ustawienie głośności lub sposób wykonywania testu.",
    "Jeśli podano wiek badanego lub rodzaj słuchawek, uwzględnij ich możliwy wpływ na wynik. Weź pod uwagę między innymi naturalne zmiany słuchu związane z wiekiem oraz różnice w jakości, charakterystyce częstotliwościowej i izolacji akustycznej słuchawek dousznych i nausznych.",
    "Nie stawiaj diagnozy medycznej i nie zakładaj przyczyny ewentualnego ubytku słuchu na podstawie samego tego testu.",
    "Jeżeli wynik wskazuje na pogorszenie słuchu na określonych częstotliwościach, wyjaśnij badanemu, jakie konsekwencje może to mieć w codziennym życiu.",
    "Określ, jakiego rodzaju dźwięki lub elementy mowy mogą być trudniejsze do usłyszenia przy stwierdzonym wzorcu ubytku. Uwzględnij w szczególności różnicę pomiędzy ubytkiem dla niskich, średnich i wysokich częstotliwości.",
    "Jeżeli występuje pogorszenie słuchu dla wysokich częstotliwości, wyjaśnij, że może ono między innymi utrudniać rozumienie niektórych głosek, szczególnie w hałasie, oraz rozróżnianie cichych i wysokoczęstotliwościowych elementów mowy.",
    "Jeżeli występuje pogorszenie słuchu dla niskich częstotliwości, wyjaśnij, jakie rodzaje dźwięków mogą być przez badanego odbierane słabiej.",
    "Jeżeli występuje istotna różnica pomiędzy uszami, wyjaśnij, że asymetria może utrudniać lokalizowanie źródła dźwięku oraz rozumienie mowy w hałasie.",
    "Nie wyciągaj wniosku o nadwrażliwości słuchowej wyłącznie na podstawie podwyższonych progów słyszenia. Nadwrażliwość na dźwięki (np. hiperakuzja) nie może być wiarygodnie rozpoznana na podstawie samego audiogramu. Jeżeli wynik lub opis badanego może sugerować problem z tolerancją dźwięków, zaznacz to jako możliwość wymagającą dalszej oceny.",
    "Jeżeli ubytek słuchu jest widoczny w zakresie częstotliwości istotnych dla rozumienia mowy i może wpływać na codzienne funkcjonowanie, oceń, czy warto rozważyć konsultację protetyka słuchu i dobór aparatu słuchowego.",
    "Nie zalecaj aparatu słuchowego automatycznie. Wyjaśnij, że decyzja o jego zastosowaniu powinna uwzględniać rzeczywisty wpływ ubytku na komunikację, wyniki profesjonalnego badania słuchu, potrzeby badanego oraz jego komfort słuchowy.",
    "Jeżeli ubytek jest niewielki lub ograniczony do określonych częstotliwości, zaznacz, że aparat słuchowy może nie być konieczny, ale warto skonsultować wynik ze specjalistą.",
    "Jeżeli ubytek jest większy i może istotnie utrudniać rozumienie mowy, szczególnie w hałasie, wskaż, że konsultacja dotycząca aparatów słuchowych może być zasadna.",

    "Zawsze zalecaj wykonanie profesjonalnego badania słuchu w placówce „Świat Słuchu”.",
    "Podkreśl, że profesjonalne badanie pozwala dokładniej ocenić słuch i zweryfikować, czy wynik uzyskany podczas testu internetowego rzeczywiście odzwierciedla stan słuchu.",

    "Dane wejściowe:",
    `Częstotliwości (Hz): ${frequencyLine}.`,
    `Lewe ucho (dB HL): ${leftLine}.`,
    `Prawe ucho (dB HL): ${rightLine}.`,
    ...demographicsLines,

    "Struktura odpowiedzi:",
    "1. Krótkie podsumowanie wyniku – określ, czy progi słyszenia mieszczą się w orientacyjnej normie, czy występują odchylenia, z uwzględnieniem możliwych błędów pomiaru.",
    "2. Osobno lewe i prawe ucho – określ orientacyjny stopień ubytku: prawidłowy / lekki / umiarkowany / znaczny. Jeśli ubytek występuje tylko w określonym zakresie częstotliwości, wyraźnie to zaznacz.",
    "3. Różnice między uszami – określ, czy występuje asymetria i czy może mieć ona znaczenie funkcjonalne.",
    "4. Wzorzec ubytku – określ, czy ubytek dotyczy głównie niskich, średnich czy wysokich częstotliwości, czy ma charakter płaski lub inny zauważalny wzorzec.",
    "5. Możliwy wpływ na codzienne słyszenie – wyjaśnij prostym językiem, jakie dźwięki, głoski lub sytuacje komunikacyjne mogą być trudniejsze do usłyszenia przy takim wyniku. Nie przedstawiaj tych informacji jako pewnika.",
    "6. Nadwrażliwość słuchowa – jeżeli są ku temu podstawy, zaznacz, że sam wynik audiometryczny nie pozwala rozpoznać nadwrażliwości na dźwięki. Nie utożsamiaj ubytku słuchu z nadwrażliwością.",
    "7. Aparat słuchowy – oceń, czy na podstawie uzyskanego wyniku warto rozważyć konsultację w kierunku aparatu słuchowego. Uzasadnij krótko swoją ocenę i zaznacz ograniczenia testu internetowego.",
    "8. Co dalej – zawsze rekomenduj profesjonalne badanie słuchu i konsultację w „Świat Słuchu”.",

    "Odpowiedź ma być zwięzła.",
    "Używaj prostego języka zrozumiałego dla osoby bez wiedzy medycznej.",
    "Nie zgaduj – jeśli danych jest za mało, zaznacz niepewność.",
    "Nie stawiaj diagnozy ani nie przypisuj konkretnej przyczyny ubytku słuchu na podstawie tego testu."
  ].join(" ");
};

export const fetchHearingResultAnalysis = async (result: HearingTestResultPayload, demographics?: ResultAnalysisDemographics) => {
  const response = await fetchWithAuth(getApiUrl("/tools/open-ai"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      text: buildHearingResultAnalysisPrompt(result, demographics)
    })
  });

  const payload = await response.json().catch(() => null) as AnalysisResponse | null;

  if (!response.ok) {
    throw new Error(payload?.error || "Nie udalo sie pobrac analizy wynikow.");
  }

  if (!payload?.content) {
    throw new Error("Serwer nie zwrocil tresci analizy.");
  }

  return payload.content;
};

export const saveAiAnalysis = async (idtonalresult: number, aiAnalysis: string): Promise<void> => {
  await fetchWithAuth(getApiUrl(`testresult/${idtonalresult}/ai-analysis`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ ai_analysis: aiAnalysis })
  });
};

// ── Verbal audiometry analysis ────────────────────────────────────────────────

export const canAnalyzeVerbalResult = (result: VerbalTestResultPayload): boolean => {
  const { srt, wrs } = result;
  return (
    srt.left !== null || srt.right !== null ||
    wrs.left !== null || wrs.right !== null
  );
};

export const buildVerbalResultAnalysisPrompt = (result: VerbalTestResultPayload, demographics?: ResultAnalysisDemographics): string => {
  const fmt = (v: number | null) => v !== null ? `${v}` : "brak";
  const demographicsLines = buildDemographicsLines(demographics);
  return [
    "Przeanalizuj wynik audiometrii słownej na podstawie poniższych danych.",
    "Odpowiedz po polsku, używając prostego, zrozumiałego języka dla pacjenta (bez żargonu medycznego).",
    "Założenia: SRT (Speech Reception Threshold) – próg rozumienia mowy w dB HL; norma ≤20 dB HL.",
    "WRS (Word Recognition Score) – wynik rozpoznawania słów w %; norma ≥90%.",
    "Ważne: Nie sugeruj jednoznacznie, że wynik jest prawidłowy – uwzględnij, że na wynik mogły wpłynąć czynniki takie jak warunki nagrania, hałas lub błędy kalibracji.",
    "Jeśli podano wiek badanego lub rodzaj słuchawek, uwzględnij ich możliwy wpływ na wynik (np. naturalny spadek słuchu wraz z wiekiem, różnice w jakości i izolacji akustycznej słuchawek dousznych i nausznych).",
    "Zawsze zalecaj wykonanie profesjonalnego badania słuchu w placówce Swiat Sluchu.",
    "Dane wejściowe:",
    `SRT lewe ucho: ${fmt(result.srt.left)} dB HL.`,
    `SRT prawe ucho: ${fmt(result.srt.right)} dB HL.`,
    `WRS lewe ucho: ${fmt(result.wrs.left)}%.`,
    `WRS prawe ucho: ${fmt(result.wrs.right)}%.`,
    ...demographicsLines,
    "Struktura odpowiedzi:",
    "1. Krótkie podsumowanie ogólne (czy wyniki sugerują normę, czy możliwy ubytek, z uwzględnieniem błędów pomiaru).",
    "2. Osobno lewe i prawe ucho – oceń SRT i WRS.",
    "3. Różnice między uszami (czy występuje asymetria).",
    "4. Krótka wskazówka co dalej (zawsze rekomenduj wizytę w Swiat Sluchu).",
    "Odpowiedź ma być zwięzła (maksymalnie 8–10 zdań).",
    "Nie zgaduj – jeśli danych jest za mało, zaznacz niepewność.",
  ].join(" ");
};

export const fetchVerbalResultAnalysis = async (result: VerbalTestResultPayload, demographics?: ResultAnalysisDemographics): Promise<string> => {
  const response = await fetchWithAuth(getApiUrl("/tools/open-ai"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text: buildVerbalResultAnalysisPrompt(result, demographics) })
  });
  const payload = await response.json().catch(() => null) as AnalysisResponse | null;
  if (!response.ok) {
    throw new Error(payload?.error || "Nie udało się pobrać analizy wyników.");
  }
  if (!payload?.content) {
    throw new Error("Serwer nie zwrócił treści analizy.");
  }
  return payload.content;
};

export const saveVerbalAiAnalysis = async (idverbalresult: number, aiAnalysis: string): Promise<void> => {
  await fetchWithAuth(getApiUrl(`verbalresult/${idverbalresult}/ai-analysis`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ai_analysis: aiAnalysis })
  });
};